import { createContext, useContext } from "react";

// If we need more robust project state persistence in future, a better solution
// would be to update the routing to include project ID in the URL.

export interface PatientProjectLocationState {
  projectId: string;
  projectTitle: string;
  patientId: string;
  patientCodeName: string;
}

interface ProjectNavigationState {
  projectId: string;
  projectTitle: string;
}

export const ProjectNavigationContext =
  createContext<ProjectNavigationState | null>(null);

export const useProjectNavigation = () => useContext(ProjectNavigationContext);

// Use module-level variable to reduce boilerplate from a context + setter pattern
// and to avoid making this niche provider an ancestor of all the consumers
let lastPatientProjectContext: PatientProjectLocationState | null = null;
let clearTimer: ReturnType<typeof setTimeout> | null = null;

const STORAGE_KEY = "patientProjectContext";

function isFreshPageLoad(): boolean {
  const entries = performance.getEntriesByType(
    "navigation"
  ) as PerformanceNavigationTiming[];
  return entries[0]?.type === "navigate";
}

export function setPatientProjectContext(ctx: PatientProjectLocationState) {
  lastPatientProjectContext = ctx;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
}

// Call from PatientShow's useEffect. On tab switch, unmount and remount happen
// in the same React commit (<1ms), so the 100ms timeout is always cancelled.
// On real navigation away, the timeout fires and clears stale context.
// On page refresh, the browser kills JS before the timeout fires, so
// sessionStorage survives and the context is restored.
export function onPatientShowMount() {
  if (clearTimer) {
    clearTimeout(clearTimer);
    clearTimer = null;
  }
}

export function onPatientShowUnmount() {
  clearTimer = setTimeout(() => {
    lastPatientProjectContext = null;
    sessionStorage.removeItem(STORAGE_KEY);
  }, 100);
}

export function getPatientProjectContext(
  patientId: string
): PatientProjectLocationState | null {
  if (lastPatientProjectContext?.patientId === patientId) {
    return lastPatientProjectContext;
  }

  // Module-level var is empty — this is a full page load.
  // On refresh/back-forward, restore from sessionStorage.
  // On fresh navigation (typed URL), clear it.
  if (!isFreshPageLoad()) {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed: PatientProjectLocationState = JSON.parse(stored);
        if (parsed.patientId === patientId) {
          lastPatientProjectContext = parsed;
          return parsed;
        }
      } catch {
        // corrupted — fall through
      }
    }
  }

  sessionStorage.removeItem(STORAGE_KEY);
  return null;
}
